import { ApolloError, gql } from "@apollo/client";
import { compose, withHooks, withStores, withTranslation } from "enhancers";
import paths from "routes/paths";

import appStore from "stores/appStore";

import { Modal, Redirect, Route, Switch } from "components";
import { BlankLayout, MenuLayout } from "layouts";

import AdminEditPage from "pages/main/admins/edit";
import AdminIndexPage from "pages/main/admins/index";
import AdminNewPage from "pages/main/admins/new";

import RoleEditPage from "pages/main/roles/edit";
import RoleIndexPage from "pages/main/roles/index";
import RoleNewPage from "pages/main/roles/new";

import SettingsIndexPage from "pages/main/settings";

import {
  getRefreshToken,
  getToken,
  hasRevokedSession,
  isAuthError,
  isConnectionError,
  removeToken,
} from "api";
import LanguagesDropdown from "components/common/LanguagesDropdown";
import { LoginPage } from "pages/main/authentication/LoginPage";
import { ResetPasswordPage } from "pages/main/authentication/ResetPassword";
import { SetupPasswordPage } from "pages/main/authentication/SetupPassword";

import { useMsal } from "@azure/msal-react";
import { PERMISSIONS } from "constants/enums/permissions";
import { ReportPage } from "pages/main/report";

import EmployeeEditPage from "pages/main/employee/edit";
import EmployeeIndexPage from "pages/main/employee/index";

import BudgetEditPage from "pages/main/budget/edit";
import BudgetIndexPage from "pages/main/budget/index";

import { setRefreshToken, setToken } from "api";
import RequestIndexPage from "pages/main/request/index";
import { getErrorMessage, homePath } from "utils/helper";
import RequestDetailPage from "pages/main/request/detail";
import FileBrowserPage from "pages/main/fileBrowser";
import PaymentCyclePage from "pages/main/paymentCycles";
import PaymentCycleDetailPage from "pages/main/paymentCycles/detail";
import HospitalImportPage from "pages/main/hospitalImport";
import HospitalImportDetailPage from "pages/main/hospitalImport/detail";
import { useIdleTimer } from "react-idle-timer";
import PartnerHospitalIndexPage from "pages/main/partnerHospitals";
import PartnerHospitalDetailPage from "pages/main/partnerHospitals/detail";
import PdfSettingPage from "pages/main/pdfSetting/PdfSetting";
import employeeDelegation from "pages/main/employeeDelegation";
import EmployeeDelegationEditPage from "pages/main/employeeDelegation/edit";
import EmployeeDelegationCreatePage from "pages/main/employeeDelegation/create";

// prettier-ignore
const InitialPages = () => (
  <BlankLayout>
    <LanguagesDropdown />
    <Switch>
      <Route path={paths.signUpPath()} exact component={LoginPage} />

      {/* <Route path={paths.setupPasswordPath()}   exact component={SetupPasswordPage} /> */}

      <Redirect to={paths.signUpPath()} />
    </Switch>
  </BlankLayout>
);

// prettier-ignore
const GuestPages = () => (
  <BlankLayout>
    <LanguagesDropdown />
    <Switch>
      <Route path={paths.signInPath()} exact component={LoginPage} />
      <Route
        path={paths.setupPasswordPath()}
        exact
        component={SetupPasswordPage}
      />
      <Route
        path={paths.resetPasswordPath()}
        exact
        component={ResetPasswordPage}
      />

      <Redirect to={paths.signInPath()} />
    </Switch>
  </BlankLayout>
);

// prettier-ignore
const MainPages = (props: any) => (
  <Switch>
    <Route
      path={paths.adminNewPath()}
      exact
      layout={MenuLayout}
      component={AdminNewPage}
      permittedRoles={["ADMIN_MANAGEMENT_READ"]}
    />
    <Route
      path={paths.adminsPath()}
      exact
      layout={MenuLayout}
      component={AdminIndexPage}
      permittedRoles={["ADMIN_MANAGEMENT_READ"]}
    />
    <Route
      path={paths.adminEditPath(":id")}
      exact
      layout={MenuLayout}
      component={AdminEditPage}
      permittedRoles={["ADMIN_MANAGEMENT_READ"]}
    />
    <Route
      path={paths.rolesPath()}
      exact
      layout={MenuLayout}
      component={RoleIndexPage}
      permittedRoles={["PERMISSION_MANAGEMENT_READ"]}
    />
    <Route
      path={paths.roleNewPath()}
      exact
      layout={MenuLayout}
      component={RoleNewPage}
      permittedRoles={["PERMISSION_MANAGEMENT_READ"]}
    />
    <Route
      path={paths.roleEditPath(":id")}
      exact
      layout={MenuLayout}
      component={RoleEditPage}
      permittedRoles={["PERMISSION_MANAGEMENT_READ"]}
    />
    <Route
      path={paths.settingsPath()}
      exact
      layout={MenuLayout}
      component={SettingsIndexPage}
      permittedRoles={["SETTING_MANAGEMENT_READ"]}
    />
    <Route
      path={paths.reportPath()}
      exact
      layout={MenuLayout}
      component={ReportPage}
      permittedRoles={[PERMISSIONS.REPORT_MANAGEMENT_READ]}
    />
    <Route
      path={paths.employeePath()}
      exact
      layout={MenuLayout}
      component={EmployeeIndexPage}
      permittedRoles={[]}
    />
    <Route
      path={paths.employeeEditPath(":id")}
      exact
      layout={MenuLayout}
      component={EmployeeEditPage}
      permittedRoles={[]}
    />
    <Route
      path={paths.budgetPath()}
      exact
      layout={MenuLayout}
      component={BudgetIndexPage}
      permittedRoles={[]}
    />
    <Route
      path={paths.budgetEditPath(":id")}
      exact
      layout={MenuLayout}
      component={BudgetEditPage}
      permittedRoles={[]}
    />
    <Route
      path={paths.requestPath()}
      exact
      layout={MenuLayout}
      component={RequestIndexPage}
      permittedRoles={[]}
    />
    <Route
      path={paths.requestDetailPath(":id")}
      exact
      layout={(props: any) => <MenuLayout {...props} removePadding />}
      component={RequestDetailPage}
      permittedRoles={[]}
    />
    <Route
      path={paths.fileBrowserPath()}
      exact
      layout={(props: any) => <MenuLayout {...props} removePadding />}
      component={FileBrowserPage}
      permittedRoles={[]}
    />
    <Route
      path={paths.paymentCycleDetailPath(":id")}
      layout={MenuLayout}
      component={PaymentCycleDetailPage}
      permittedRoles={[PERMISSIONS.PAYMENT_CYCLE_READ]}
    />
    <Route
      path={paths.paymentCyclePath()}
      exact
      layout={MenuLayout}
      component={PaymentCyclePage}
      permittedRoles={[PERMISSIONS.PAYMENT_CYCLE_READ]}
    />
    <Route
      path={paths.hospitalImportPath()}
      exact
      layout={MenuLayout}
      component={HospitalImportPage}
      // permittedRoles={[PERMISSIONS.PARTNER_HOSPITAL_MANAGEMENT_READ]}
      permittedRoles={[PERMISSIONS.HOSPITAL_IMPORT_READ]}
    />
    <Route
      path={paths.hospitalImportDetailPath(":id")}
      exact
      layout={MenuLayout}
      component={HospitalImportDetailPage}
      // permittedRoles={[PERMISSIONS.PARTNER_HOSPITAL_MANAGEMENT_READ]}
      permittedRoles={[PERMISSIONS.HOSPITAL_IMPORT_READ]}
    />
    <Route
      path={paths.partnerHospitalPath()}
      exact
      layout={MenuLayout}
      component={PartnerHospitalIndexPage}
      // permittedRoles={[PERMISSIONS.PARTNER_HOSPITAL_MANAGEMENT_READ]}
      permittedRoles={[PERMISSIONS.PARTNER_MANAGEMENT_READ]}
    />
    <Route
      path={paths.partnerHospitalCreatePath()}
      exact
      layout={MenuLayout}
      component={PartnerHospitalDetailPage}
      permittedRoles={[PERMISSIONS.PARTNER_MANAGEMENT_READ]}
    />
    <Route
      path={paths.partnerHospitalDetailPath(":id")}
      exact
      layout={MenuLayout}
      component={PartnerHospitalDetailPage}
      permittedRoles={[PERMISSIONS.PARTNER_MANAGEMENT_READ]}
    />
    <Route
      path={paths.pdfSettingPath()}
      exact
      layout={MenuLayout}
      component={PdfSettingPage}
      permittedRoles={[PERMISSIONS.PARTNER_MANAGEMENT_READ]}
    />
    <Route
      path={paths.employeeDelegationPath()}
      exact
      layout={MenuLayout}
      component={employeeDelegation}
      permittedRoles={[PERMISSIONS.AGENT_MANAGEMENT_READ]}
    />
    <Route
      path={paths.employeeDelegationCreatePath()}
      exact
      layout={MenuLayout}
      component={EmployeeDelegationCreatePage}
      permittedRoles={[PERMISSIONS.AGENT_MANAGEMENT_EDIT]}
    />
    <Route
      path={paths.employeeDelegationEditPath(":id")}
      exact
      layout={MenuLayout}
      component={EmployeeDelegationEditPage}
      permittedRoles={[PERMISSIONS.AGENT_MANAGEMENT_READ]}
    />

    <Redirect
      withPermissions
      to={homePath(props.currentUser?.role?.permissions)}
    />
  </Switch>
);

interface RoutesProps {
  currentUser: object;
  initialized: boolean;
  hasFirstAdmin: boolean;
  isAuthorized: boolean;
  isAdmin: boolean;
}

// prettier-ignore
const Routes = (props: RoutesProps) => {
  if (!props.initialized) {
    return null;
  } else if (!props.hasFirstAdmin) {
    return <InitialPages />;
  } else if (!props.isAuthorized) {
    return <GuestPages />;
  } else {
    return <MainPages currentUser={props.currentUser} />;
  }
};

export const API = {
  GET_APP_INFO: gql`
    query GET_APP_INFO {
      info {
        hasFirstAdmin
      }
    }
  `,
  GET_CURRENT_USER: gql`
    query GET_CURRENT_USER {
      getCurrentBackofficeUser {
        id
        firstName
        lastName
        ownerType
        email
        admin {
          id
          code
          firstName
          lastName
        }
        role {
          title
          permissions
        }
      }
    }
  `,
  SIGN_IN_WITH_AZURE_AD: gql`
    mutation SIGN_IN_WITH_AZURE_AD($email: String!) {
      signInWithAzureAd(email: $email) {
        currentUser {
          id
          email
          authenticationToken
          role {
            permissions
          }
        }
        accessToken
        refreshToken
      }
    }
  `,
};

// กันจอค้าง: ถ้าเกินเวลานี้แล้วยังไม่พร้อมใช้งาน แปลว่ามีอะไรค้างที่กู้เองไม่ได้
// (query ไม่ settle / MSAL ไม่ resolve) ยอมปล่อยให้ผู้ใช้เห็นหน้า login พร้อม
// modal ให้กดลองใหม่ ดีกว่าปล่อยให้หมุนไปเรื่อย ๆ จนต้องปิดเปิด browser เอง
const INITIALIZE_TIMEOUT = 30000;

const enhancer = compose(
  withStores((stores: { appStore: { currentUser: any } }) => ({
    currentUser: stores.appStore.currentUser,
  })),
  withTranslation({ prefix: "api" }),
  withHooks((props: any, hooks: any) => {
    const { currentUser, t } = props;
    const {
      useMemo,
      useEffect,
      useLazyQuery,
      useState,
      useMutation,
      useCallback,
    } = hooks;
    const { accounts, instance, inProgress } = useMsal();
    const [isReady, setIsReady] = useState(false);
    const [isMsalLoading, setIsMsalLoading] = useState(true);
    const [initializeFailed, setInitializeFailed] = useState(false);

    const [
      fetchAppInfo,
      { loading: appInfoLoading, data: appInfoData },
    ] = useLazyQuery(API.GET_APP_INFO, {
      onError: (error: ApolloError) => {
        console.log("GET APP INFO ERROR : ", error);
        if (isConnectionError(error)) {
          setInitializeFailed(true);
        }
      },
    });
    const [
      fetchCurrentUser,
      { loading, error: currentUserError, data: currentUserData },
    ] = useLazyQuery(API.GET_CURRENT_USER, {
      fetchPolicy: "network-only",
      onCompleted: (data: any) => {
        appStore.setCurrentUser(data.getCurrentBackofficeUser);
        setIsReady(true);
      },
      skipSetError: true,
      onError: (error: ApolloError) => {
        console.log("GET CURRENT USER ERROR : ", error);
        appStore.setCurrentUser(null);
        // ทิ้ง token เฉพาะตอน server บอกว่าใช้ไม่ได้จริง ๆ ถ้าแค่ต่อไม่ติด
        // (server restart) แล้วลบทิ้ง ผู้ใช้จะโดนเตะออกทั้งที่ session ยังดีอยู่
        if (isAuthError(error)) {
          removeToken();
        }
        if (isConnectionError(error)) {
          setInitializeFailed(true);
        }
        // เช็ค auth จบแล้ว (ผลคือไม่ผ่าน) ต้องบอกว่าพร้อม render หน้า login
        // ถ้าตั้ง false ค่าจะเท่าเดิมกับ initial state = ไม่ trigger re-render
        // แล้วต้องไปหวังพึ่ง re-render จาก Apollo อย่างเดียว
        setIsReady(true);
      },
    });

    const errorModal = useCallback(
      (message: string) => {
        // show modal on error when signin via azure
        // @ts-ignore
        return Modal.alert({
          className: "ErrorModal",
          title: t(".modal500Title"),
          children: message,
          okButtonLabel: t(".modal500OkButtonLabel"),
          onOk: async ({ ...props }) => {
            sessionStorage.clear();
            window.location.reload();
            // @ts-ignore
            props.close();
          },
          okButtonVariant: "contained",
        });
      },
      [t]
    );

    const [signInWithAzureAd] = useMutation(API.SIGN_IN_WITH_AZURE_AD, {
      onCompleted: (data: any) => {
        console.log("signin via azure Ad => complete : ", data);
        const {
          accessToken,
          refreshToken,
          currentUser,
        } = data.signInWithAzureAd;
        appStore.setCurrentUser(currentUser);
        setToken(accessToken);
        setRefreshToken(refreshToken);
        if (accessToken && refreshToken) {
          fetchCurrentUser();
          fetchAppInfo();
        }
        setIsMsalLoading(false);
      },
      skipSetError: true,
      onError: (error: any) => {
        console.log("signin via azure Ad => error : ", error);

        // network error จะไม่มี graphQLErrors ถ้า optional chain ไม่ครบสาย
        // บรรทัดนี้จะโยน TypeError แล้ว setIsMsalLoading(false) ข้างล่างจะไม่ถูก
        // เรียก = splash ค้างถาวร
        const message =
          error?.graphQLErrors?.[0]?.extensions?.extensions?.originalError?.[0]
            ?.message;

        errorModal(message ? t(message) : getErrorMessage(error));
        setIsMsalLoading(false);
        setIsReady(true);
      },
    });

    const token = getToken();
    const refreshToken = getRefreshToken();

    useEffect(() => {
      if (token && refreshToken) {
        fetchCurrentUser();
      } else {
        setIsReady(true);
      }
    }, [fetchCurrentUser, token, refreshToken]);
    useEffect(() => {
      fetchAppInfo();
    }, [fetchAppInfo]);

    const pageLoading = useMemo(
      () => loading || isMsalLoading || appInfoLoading,
      [isMsalLoading, loading, appInfoLoading]
    );

    const ready = !pageLoading && isReady;
    const initialized = ready || initializeFailed;
    const hasFirstAdmin = !pageLoading && !!appInfoData;
    const isAuthorized = !!(!currentUserError && currentUserData);

    const ownerType = useMemo(() => {
      if (loading || currentUserError) {
        return null;
      }
      return currentUserData?.getCurrentBackofficeUser?.ownerType;
    }, [loading, currentUserData, currentUserError]);

    useEffect(() => {
      const $splashScreen = document.getElementById("splash-screen");
      if ($splashScreen) {
        const display = initialized ? "none" : "";
        $splashScreen.style.display = display;
      }
    }, [initialized]);

    useEffect(() => {
      if (ready) {
        return;
      }
      const timer = setTimeout(() => {
        console.warn(
          `[Routes] ยังไม่พร้อมใช้งานภายใน ${INITIALIZE_TIMEOUT}ms - บังคับปิด splash`
        );
        setInitializeFailed(true);
      }, INITIALIZE_TIMEOUT);
      return () => clearTimeout(timer);
    }, [ready]);

    useEffect(() => {
      if (!initializeFailed) {
        return;
      }
      // @ts-ignore
      Modal.alert({
        className: "InitializeFailed",
        title: t(".modalConnectionFailedTitle"),
        children: t(".modalConnectionFailedChildren"),
        okButtonLabel: t(".modalConnectionFailedOkButtonLabel"),
        okButtonVariant: "contained",
        onOk: async ({ ...modalProps }: any) => {
          window.location.reload();
          // @ts-ignore
          modalProps.close();
        },
        disableBackdropClick: true,
      });
    }, [initializeFailed, t]);

    useEffect(() => {
      appStore.setHasFirstAdmin(hasFirstAdmin);
    }, [hasFirstAdmin]);

    useEffect(() => {
      instance
        .handleRedirectPromise()
        .then(async () => {
          const account = accounts[0];
          const currentToken = getToken();

          // โดนเตะออกเพราะ login ซ้อน - ห้าม auto sign-in ต่อ ไม่งั้นเครื่องนี้จะ
          // แย่ง session กลับจากเครื่องที่เพิ่ง login แล้วเตะกันไปมาไม่จบ
          if (hasRevokedSession()) {
            setIsMsalLoading(false);
            return;
          }

          if (account && !currentToken) {
            setIsReady(false);
            await signInWithAzureAd({
              variables: {
                email: account.idTokenClaims?.preferred_username,
              },
            });
          } else {
            setIsMsalLoading(false);
          }
        })
        .catch((err) => {
          // MSAL reject ได้หลายทาง (interaction_in_progress, state หมดอายุ,
          // hash ค้างจาก session ก่อนหน้า) ถ้าไม่ปลดสองตัวนี้ splash จะค้างถาวร
          console.log(err);
          setIsMsalLoading(false);
          setIsReady(true);
        });
    }, [accounts, instance, signInWithAzureAd]);

    const isAdmin = ownerType === "Admin";

    const hasAccount = useMemo(() => {
      return accounts.length > 0;
    }, [instance, accounts, inProgress]);

    const handleClickLogout = useCallback(() => {
      if (hasAccount) {
        instance.logout({
          account: accounts[0],
          onRedirectNavigate: (url) => {
            instance.setActiveAccount(null);
            removeToken();
            return true;
          },
        });
        instance.setActiveAccount(null);
      } else {
        removeToken();
      }
    }, [hasAccount, instance, accounts]);

    const expireTimeAlert = () => {
      handleClickLogout();
      // @ts-ignore
      Modal.alert({
        title: "หมดเวลาการใช้งาน",
        children:
          "กรุณาเข้าสู่ระบบอีกครั้ง เนื่องจากไม่ได้มีการใช้งานระบบเป็นระยะเวลา 15 นาที",
        okButtonLabel: "เข้าสู่ระบบอีกครั้ง",
        okButtonVariant: "contained",
        onOk: async () => {
          window.location.reload();
          // @ts-ignore
          Modal.close();
        },
        disableBackdropClick: true,
      });
    };

    const onIdle = () => {
      const token = getToken();
      if (token) expireTimeAlert();
    };

    const onActive = () => {};
    const onAction = () => {
      localStorage.setItem("lastActiveTime", new Date().toISOString());
    };
    const { getRemainingTime, getLastActiveTime } = useIdleTimer({
      onIdle,
      onActive,
      onAction,
      crossTab: true,
      timeout: 1000 * 900,
      events: [
        "mousemove",
        "keydown",
        "mousedown",
        "touchstart",
        "wheel",
        "mousewheel",
      ],
    });

    useEffect(() => {
      const lastActiveTime = localStorage.getItem("lastActiveTime");

      const token = getToken();
      if (lastActiveTime !== null && token) {
        const lastActiveTimeExpire =
          new Date().getTime() - new Date(lastActiveTime).getTime();

        if (lastActiveTimeExpire >= 1000 * 900) {
          handleClickLogout();
        }
      }
    }, []);

    return {
      currentUser,
      initialized,
      isAuthorized,
      hasFirstAdmin,
      isAdmin,
    };
  })
);

export default enhancer(Routes);
